import { useEffect, useState, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, GraduationCap, Printer, Calendar, TrendingUp, ClipboardCheck, MessageSquare, Award, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useStudentAuth, studentApi } from "@/lib/studentAuth";
import { formatApiError } from "@/lib/api";

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
        <Icon size={14} className="text-[#FF6B35]" /> {label}
      </div>
      <div className="mt-3 font-display text-3xl font-800 text-[#0B192C]">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function TypeBadge({ t }) {
  const map = {
    test: { l: "Test", c: "bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/30" },
    attendance: { l: "Attendance", c: "bg-blue-50 text-blue-700 border-blue-200" },
    remark: { l: "Remark", c: "bg-slate-100 text-slate-700 border-slate-200" },
    grade: { l: "Grade", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  };
  const m = map[t] || map.remark;
  return <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border ${m.c}`}>{m.l}</span>;
}

export default function StudentPortal() {
  const { student, ready, logout } = useStudentAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!student || !student.id) return;
    (async () => {
      try {
        const { data } = await studentApi.get("/students/me/progress");
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [student]);

  const subjectStats = useMemo(() => {
    if (!data) return [];
    const bySubject = {};
    data.entries.filter(e => e.entry_type === "test" && e.subject && e.percentage != null).forEach(e => {
      if (!bySubject[e.subject]) bySubject[e.subject] = { subject: e.subject, count: 0, sum: 0, best: 0 };
      bySubject[e.subject].count += 1;
      bySubject[e.subject].sum += e.percentage;
      bySubject[e.subject].best = Math.max(bySubject[e.subject].best, e.percentage);
    });
    return Object.values(bySubject).map(s => ({ ...s, avg: Math.round((s.sum / s.count) * 100) / 100 }));
  }, [data]);

  if (!ready) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!student || !student.id) return <Navigate to="/student/login" replace />;

  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-area { padding: 0 !important; }
          .card-print { break-inside: avoid; }
        }
      `}</style>

      {/* Header */}
      <header className="no-print bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-10 h-10 rounded-md bg-white/5 border border-white/10 grid place-items-center">
              <GraduationCap size={22} className="text-[#FF6B35]" />
            </span>
            <div>
              <div className="font-display font-bold uppercase tracking-tight">Vikramshila Classes</div>
              <div className="text-[10px] uppercase tracking-widest text-[#FF6B35] font-semibold">Student Portal</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium">{student.name}</div>
              <div className="text-xs text-white/60">Class {student.student_class} · {student.board}</div>
            </div>
            <button onClick={() => window.print()} data-testid="print-report" className="vk-btn-outline text-sm">
              <Printer size={14} /> Save as PDF
            </button>
            <button onClick={logout} data-testid="student-logout" className="vk-btn-outline text-sm">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="print-area max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* Report title (visible in print) */}
        <div className="card-print">
          <div className="vk-chip">Progress Report</div>
          <h1 className="mt-3 font-display text-4xl font-800 text-[#0B192C]">Hello, {student.name.split(" ")[0]} 👋</h1>
          <p className="mt-2 text-slate-600">Class {student.student_class} · {student.board} · Phone {student.phone}</p>
          <div className="mt-1 text-xs text-slate-500">Report generated on {new Date().toLocaleString()}</div>
        </div>

        {/* Stats */}
        <section className="card-print grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={TrendingUp} label="Average test %" value={summary.average_percentage != null ? `${summary.average_percentage}%` : "—"} hint={summary.tests ? `Across ${summary.tests} tests` : "No tests yet"} />
          <StatCard icon={ClipboardCheck} label="Attendance" value={summary.average_attendance != null ? `${summary.average_attendance}%` : "—"} />
          <StatCard icon={Award} label="Total entries" value={summary.total_entries || 0} />
          <StatCard icon={BookOpen} label="Subjects tracked" value={subjectStats.length} />
        </section>

        {/* Subject-wise */}
        {subjectStats.length > 0 && (
          <section className="card-print">
            <h2 className="font-display text-2xl font-800 text-[#0B192C]">Subject-wise performance</h2>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {subjectStats.map((s) => (
                <div key={s.subject} className="rounded-lg bg-white border border-slate-200 p-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500">{s.subject}</div>
                  <div className="mt-2 font-display text-3xl font-800 text-[#0B192C]">{s.avg}%</div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                    <span>Best: <b className="text-[#FF6B35]">{s.best}%</b></span>
                    <span>Tests: <b>{s.count}</b></span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* All entries */}
        <section className="card-print">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-800 text-[#0B192C]">All updates</h2>
            <div className="text-sm text-slate-500 flex items-center gap-1"><Calendar size={14} /> Latest first</div>
          </div>
          {loading ? (
            <div className="mt-4 text-slate-500">Loading…</div>
          ) : data && data.entries.length === 0 ? (
            <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500">
              No entries yet. Your teacher will add tests, attendance and remarks soon — check back after your next class.
            </div>
          ) : (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white overflow-hidden">
              <ul className="divide-y divide-slate-100">
                {data.entries.map((e) => (
                  <li key={e.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6" data-testid={`entry-${e.id}`}>
                    <div className="sm:w-28"><TypeBadge t={e.entry_type} /></div>
                    <div className="flex-1 min-w-0">
                      {e.entry_type === "test" && (
                        <>
                          <div className="font-medium text-[#0B192C]">{e.test_name || `${e.subject || "Test"} — ${e.chapter || "General"}`}</div>
                          <div className="text-sm text-slate-600 mt-0.5">
                            {e.subject && <span>{e.subject}{e.chapter ? ` · ${e.chapter}` : ""}</span>}
                            {e.marks_obtained != null && e.max_marks != null && (
                              <span> · <b>{e.marks_obtained}</b> / {e.max_marks} ({e.percentage}%)</span>
                            )}
                          </div>
                        </>
                      )}
                      {e.entry_type === "attendance" && (
                        <>
                          <div className="font-medium text-[#0B192C]">Attendance {e.attendance_percent != null && `— ${e.attendance_percent}%`}</div>
                          {e.remark && <div className="text-sm text-slate-600 mt-0.5">{e.remark}</div>}
                        </>
                      )}
                      {e.entry_type === "remark" && (
                        <>
                          <div className="font-medium text-[#0B192C] flex items-center gap-2"><MessageSquare size={14} className="text-[#FF6B35]" /> Teacher&apos;s remark</div>
                          <div className="text-sm text-slate-700 mt-1 italic">&ldquo;{e.remark}&rdquo;</div>
                        </>
                      )}
                      {e.entry_type === "grade" && (
                        <>
                          <div className="font-medium text-[#0B192C]">Grade {e.subject && `— ${e.subject}`} : <span className="text-[#FF6B35]">{e.grade_value}</span></div>
                          {e.remark && <div className="text-sm text-slate-600 mt-0.5">{e.remark}</div>}
                        </>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">
                      {e.entry_date || new Date(e.created_at).toLocaleDateString()}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
